import React, { useMemo, useState } from "react";
import { Container, Row, Col, Input } from "reactstrap";

import { MaterialReactTable } from "material-react-table";

const STATUS_STYLES = {
  Successful: { bg: "#E7F7EF", color: "#1F9254", dot: "#1F9254" },
  Failed: { bg: "#FDEDEC", color: "#E5484D", dot: "#E5484D" },
  Cancelled: { bg: "#F1F1F1", color: "#6B7280", dot: "#6B7280" },
};

const TABS = ["All", "Successful", "Failed", "Cancelled"];

function formatCurrency(value) {
  if (!value) return "—";

  return `₹${Number(value).toLocaleString("en-IN")}`;
}

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.Cancelled;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "20px",
        background: style.bg,
        color: style.color,
        fontSize: "13px",
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: style.dot,
        }}
      />

      {status}
    </span>
  );
}

function TransactionsListings() {
  const [activeTab, setActiveTab] = useState("All");

  const [searchText, setSearchText] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const STATIC_DATA = [
    {
      transactionid: "TXN-8821",
      datetime: "03 Jul 2026, 10:47 am",
      customername: "Priya Sharma",
      customerid: "CUST-441",
      offer: "20% Off on Electronics",
      billamount: 2500,
      points: 500,
      amountpaid: 2000,
      payment: "UPI",
      status: "Successful",
    },
    {
      transactionid: "TXN-8820",
      datetime: "03 Jul 2026, 10:31 am",
      customername: "Arjun Mehta",
      customerid: "CUST-219",
      offer: "Flat ₹150 Off Groceries",
      billamount: 900,
      points: 200,
      amountpaid: 700,
      payment: "Card, Cash",
      status: "Successful",
    },
    {
      transactionid: "TXN-8819",
      datetime: "03 Jul 2026, 09:58 am",
      customername: "Sneha Kapoor",
      customerid: "CUST-887",
      offer: "20% Off on Electronics",
      billamount: 5200,
      points: null,
      amountpaid: null,
      payment: null,
      status: "Failed",
    },
    {
      transactionid: "TXN-8818",
      datetime: "03 Jul 2026, 09:22 am",
      customername: "Rohit Nair",
      customerid: "CUST-552",
      offer: "Flat ₹200 Off Home Appliances",
      billamount: 4500,
      points: 400,
      amountpaid: 4100,
      payment: "Elytra Points, UPI",
      status: "Successful",
    },
    {
      transactionid: "TXN-8817",
      datetime: "02 Jul 2026, 06:44 pm",
      customername: "Divya Rao",
      customerid: "CUST-114",
      offer: "Flat ₹150 Off Groceries",
      billamount: 1100,
      points: 200,
      amountpaid: 900,
      payment: "UPI",
      status: "Successful",
    },
    {
      transactionid: "TXN-8816",
      datetime: "02 Jul 2026, 04:11 pm",
      customername: "Kiran Bose",
      customerid: "CUST-773",
      offer: "20% Off on Electronics",
      billamount: 3400,
      points: 500,
      amountpaid: 2900,
      payment: "Card",
      status: "Successful",
    },
    {
      transactionid: "TXN-8815",
      datetime: "02 Jul 2026, 02:55 pm",
      customername: "Meena Iyer",
      customerid: "CUST-302",
      offer: "Flat ₹200 Off Home Appliances",
      billamount: 6200,
      points: null,
      amountpaid: null,
      payment: null,
      status: "Cancelled",
    },
    {
      transactionid: "TXN-8814",
      datetime: "02 Jul 2026, 12:30 pm",
      customername: "Suresh Pillai",
      customerid: "CUST-661",
      offer: "Flat ₹150 Off Groceries",
      billamount: 800,
      points: 200,
      amountpaid: 600,
      payment: "Cash",
      status: "Successful",
    },
  ];

  const data = useMemo(() => {
    let result = STATIC_DATA;

    if (activeTab !== "All") {
      result = result.filter((item) => item.status === activeTab);
    }

    if (searchText.trim()) {
      const search = searchText.toLowerCase();

      result = result.filter(
        (item) =>
          item.transactionid.toLowerCase().includes(search) ||
          item.customername.toLowerCase().includes(search) ||
          item.customerid.toLowerCase().includes(search) ||
          item.offer.toLowerCase().includes(search),
      );
    }

    return result;
  }, [activeTab, searchText]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "transactionid",
        header: "Transaction",
        size: 180,

        Cell: ({ row }) => (
          <>
            <div
              style={{
                fontWeight: 700,
                color: "#2b2b2b",
              }}
            >
              {row.original.transactionid}
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#9AA0A6",
              }}
            >
              {row.original.datetime}
            </div>
          </>
        ),
      },

      {
        accessorKey: "customername",
        header: "Customer",
        size: 180,

        Cell: ({ row }) => (
          <>
            <div
              style={{
                fontWeight: 600,
              }}
            >
              {row.original.customername}
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#9AA0A6",
              }}
            >
              {row.original.customerid}
            </div>
          </>
        ),
      },

      {
        accessorKey: "offer",
        header: "Offer",
        size: 220,
      },

      {
        accessorKey: "billamount",
        header: "Bill Amount",
        size: 130,

        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },

      {
        accessorKey: "points",
        header: "Points",

        Cell: ({ cell }) => (cell.getValue() ? `${cell.getValue()} pts` : "—"),
      },

      {
        accessorKey: "amountpaid",
        header: "Amount Paid",

        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },

      {
        accessorKey: "payment",
        header: "Payment",

        Cell: ({ cell }) => cell.getValue() || "—",
      },

      {
        accessorKey: "status",
        header: "Status",

        Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
      },
    ],
    [],
  );

  return (
    <div id="csms">
      <Container fluid>
        <Row>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
            <div
              className="d-flex align-items-center gap-1 flex-wrap p-1"
              style={{
                background: "#F4F6FA",
                borderRadius: "8px",
              }}
            >
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);

                    setPagination({
                      ...pagination,
                      pageIndex: 0,
                    });
                  }}
                  style={{
                    border:
                      activeTab === tab
                        ? "1px solid white"
                        : "1px solid transparent",

                    background: activeTab === tab ? "#fff" : "transparent",
                    color: activeTab === tab ? "#FD7545" : "#6B7280",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            <Input
              placeholder="Search ID, offer or customer..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                maxWidth: "320px",
                height: "40px",
              }}
            />
          </div>

          <Col lg={12}>
            <div
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <MaterialReactTable
                columns={columns}
                data={data}
                enableColumnActions={false}
                enableColumnDragging={false}
                enableColumnOrdering={false}
                enableDensityToggle={false}
                enableTopToolbar={false}
                manualPagination={false}
                onPaginationChange={setPagination}
                state={{
                  pagination,
                }}
                muiTablePaginationProps={{
                  rowsPerPageOptions: [10, 20, 50],
                }}
                muiTableHeadCellProps={{
                  sx: {
                    backgroundColor: "#F4F6FA",
                  },
                }}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default TransactionsListings;
